import React, { createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { authClient } from "../config/auth";
import LoadingFallback from "@/components/app/LoadingFallback";
import { getAuthTokenAsync } from "@/utils/auth/tokenManager";
import AcceptInvitationPage from "@/pages/AcceptInvitation";
import { Navigate, useLocation } from "react-router-dom";

interface AuthContextType {
    sessionData: any;
    sessionIsPending: boolean;
    sessionError: any;
    sessionRefetch: () => void;
    sessionSignOut: () => void;
    getSessionToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const { data, isPending, error, refetch } = authClient.useSession();
    const { signOut } = authClient;

    // Get current route to check if the user is on invitation acceptance page
    const location = useLocation();

    // Always call hooks in same order:
    useEffect(() => {
        if (!data?.user) return;

        try {
            const { user } = data;
            const storedWorkspaceId = localStorage.getItem("workspaceId");
            const storedClientId = localStorage.getItem("clientId");

            // Only write if values are present and different
            if (user.defaultWorkspaceId && user.defaultWorkspaceId !== storedWorkspaceId) {
                localStorage.setItem("workspaceId", user.defaultWorkspaceId);
            }
            if (user.clientId && user.clientId !== storedClientId) {
                localStorage.setItem("clientId", user.clientId);
            }

            if (user.id) {
                localStorage.setItem("userId", user.id);
            }
            if (user.email) {
                localStorage.setItem("userEmail", user.email);
            } else if (Array.isArray(user.emailAddresses) && user.emailAddresses.length > 0) {
                localStorage.setItem("userEmail", user.emailAddresses[0]?.emailAddress || "");
            }
            if (user.firstName || user.lastName) {
                localStorage.setItem("userName", `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim());
            } else if (user.fullName) {
                localStorage.setItem("userName", user.fullName);
            }
            if (user.role) {
                localStorage.setItem("userRole", user.role);
                localStorage.setItem("role", user.role);
            }
        } catch (e) {
            // localStorage may throw in some environments; fail silently
            console.warn("localStorage error", e);
        }
    }, [data]);

    const getSessionToken = useCallback(async () => {
        return getAuthTokenAsync();
    }, []);

    const value = useMemo(
        () => ({
            sessionData: data,
            sessionIsPending: isPending,
            sessionError: error,
            sessionRefetch: refetch,
            sessionSignOut: signOut,
            getSessionToken,
        }),
        [data, isPending, error, refetch, signOut, getSessionToken]
    );

    if (!data && !isPending) {
        // Optional: Save where they were trying to go so you can redirect back later
        return <Navigate to="/sign-in" state={{ from: location }} replace />;
    }

    if (isPending) {
        return (
            <div className="flex items-center justify-center h-full">
                <LoadingFallback />
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};